/*
 * A person, drawn one way (X-A3 A2).
 *
 * Eight surfaces show people — the timeline, the sidebar's direct rows, Activity, a search hit, the
 * quick switcher, the administration table, the directory and a group's member list — and before
 * this file each of them decided for itself how big the picture is, whether the handle is shown,
 * where the name truncates and what a click on it does. Six of the eight differed; two drew no
 * picture at all. Here the decision is made once and the surfaces choose only the size and whether
 * the handle and the status glyph have room.
 *
 * The family has three members because a person is not always drawn as one object: a message row
 * puts the face in the list's gutter column and the name on the header line two grid cells away,
 * and an Activity row's sentence already carries the name. So the face and the name are separate
 * components that agree, and the chip is the two of them side by side — which is what every surface
 * that draws a person in one place uses.
 *
 * Wherever a face or a name stands for a real account it is also a way *to* that account: the
 * pointer gets the hover card after 300 ms (HoverCard.tsx), the click and the keyboard get the
 * profile in the details column. A surface that must not do either — the picker inside a dialog,
 * the card the hover card itself draws — hands over neither opener and gets plain text.
 */

import type { ReactNode } from "react"
import { cx } from "../../lib/cx.ts"
import { Button } from "../../ui/index.ts"
import { formatTimeIn } from "../i18n/format.ts"
import { t, type PlainKey } from "../i18n/index.ts"
import { useNow } from "../i18n/useNow.ts"
import { inPlaceClick, useDetails, useModel } from "../shell/index.ts"
import type { PublicUser } from "../store/model.ts"
import { Avatar, type AvatarSize } from "./Avatar.tsx"
import { HoverCard } from "./HoverCard.tsx"
import { directMessagePath } from "./navigation.ts"
import "./people.css"
import { accountBadge, profileSubtitle } from "./person.ts"
import { StatusLine, StatusMark } from "./PersonStatus.tsx"
import { usePresence, withAway, type Presence } from "./presence.ts"
import { statusOf, type Status } from "./status.ts"

/**
 * What every surface knows about the person it draws. It is deliberately not `PublicUser`: half of
 * the eight have a name and an id out of a map and no account row at all, and a chip that demanded
 * one would send them looking the account up for nothing.
 */
export interface ChipPerson {
	/**
	 * The account this is a picture of. Zero means "no account behind it" — a group, a placeholder, an
	 * author the model no longer knows — and then the face stays the neutral square and no card opens.
	 */
	id: number
	name: string
	handle?: string | undefined
	avatarUrl?: string | undefined
	/** "Bot", "Guest", "Deactivated", "Invited" — the pill beside the name; an ordinary account has none. */
	badge?: string | undefined
	/**
	 * Whether that pill is the warning one. An invitation nobody has answered yet is the one thing
	 * about an account a reader is about to act on — a mention of that person will not be read — while
	 * *Bot* and *Deactivated* describe a row rather than warning about it.
	 */
	badgeTone?: "warning" | undefined
	/**
	 * What this person published about themselves (`status.ts`), and their own claim to be away.
	 *
	 * They are two facts and not one because they behave as two: the status is drawn and the claim
	 * moves the dot, and the claim outlives the status — the expiry job clears the emoji and the line
	 * and leaves the claim standing. Both are filled only where the surface was handed an account row
	 * to build the chip from, which is why the timeline's author line — a name and a picture out of
	 * two maps — stays as quiet as it was.
	 */
	status?: Status | null | undefined
	away?: boolean | undefined
}

/** The chip of an account the reader has in their model. */
export function chipPerson(user: PublicUser): ChipPerson {
	return {
		id: user.id,
		name: user.displayName,
		handle: user.handle,
		avatarUrl: user.avatarUrl,
		status: statusOf(user),
		away: user.statusAway === true,
		...badgeOf(user),
	}
}

/**
 * The pill beside a name, where the account has one. *Invited* is the projection's own state
 * (`internal/core/auth/projection.go`: an account nobody has signed in to yet), and the only one
 * that warns rather than describes.
 */
function badgeOf(user: PublicUser): Pick<ChipPerson, "badge" | "badgeTone"> {
	if (user.state === "invited") return { badge: t("people.state.invited"), badgeTone: "warning" }
	return { badge: accountBadge(user) }
}

/**
 * The pill of an account, for a name drawn without a chip: a channel's member list, a direct
 * conversation's head and its row in the sidebar, the quick switcher, the mention suggestions. A
 * guest is somebody outside the team, and every place that leads to writing to them says so.
 */
export function AccountPill({ user }: { user: PublicUser | undefined }) {
	if (user === undefined) return null
	return <PersonBadge person={{ id: user.id, name: user.displayName, ...badgeOf(user) }} />
}

/** The pill itself, in the two places a name is drawn with one. */
function PersonBadge({ person }: { person: ChipPerson }) {
	if (person.badge === undefined) return null
	return (
		<span className={cx("tb-pill", person.badgeTone === "warning" && "tb-pill--warning")}>
			{person.badge}
		</span>
	)
}

/**
 * The props every drawing of a face shares. The id is handed over only for a real account: the ramp's
 * colour stands for one person, so a group or a placeholder keeps the neutral square instead of
 * borrowing somebody's tone (Avatar.tsx).
 */
function faceProps(person: ChipPerson): { id?: number; name: string; url?: string | undefined } {
	return person.id === 0
		? { name: person.name, url: person.avatarUrl }
		: { id: person.id, name: person.name, url: person.avatarUrl }
}

/** The family's own class first, then whatever the surface adds to it. */
function withClass(own: string, extra: string | undefined): string {
	return extra === undefined ? own : `${own} ${extra}`
}

/** The word for a presence, for the two surfaces that say it rather than drawing it. */
const PRESENCE_LABELS: Record<Presence, PlainKey> = {
	online: "people.presence.online",
	away: "people.presence.away",
}

/**
 * Opens a person's profile in the details column. Every surface inside the shell uses it, so that
 * "click a name, see the person" is one behaviour and not eight (UX §2.1, goal item 1).
 */
export function useOpenProfile(): (userId: number) => void {
	const details = useDetails()
	return (userId: number) => details.toggle({ view: "core.profile", params: { userId } })
}

interface PersonPartProps {
	person: ChipPerson
	/** What a click does; without it the part is text and not a control. */
	onOpen?: ((userId: number) => void) | undefined
	/** Whether the pointer may summon the hover card. Off inside dialogs and inside the card. */
	hover?: boolean
	/** The tab order of the timeline's roving list; elsewhere the default is right. */
	tabIndex?: number | undefined
	/** The surface's own class, for a part that has to fit a grid it did not design. */
	className?: string
	/** Anything the surface says beside the name — the directory's role pill. */
	after?: ReactNode
	/**
	 * Whether this surface has room for the status glyph beside the name, the way `handle` says
	 * whether it has room for the handle. It is asked for rather than assumed: a status belongs where
	 * a person is *looked up* and not in the timeline, where an emoji per author line is noise beside
	 * the message it sits over.
	 */
	status?: boolean
}

/** The picture, with the dot when this person's state is known. */
export function PersonFace({
	person,
	size = "sm",
	onOpen,
	hover = true,
	tabIndex,
	className,
}: PersonPartProps & { size?: AvatarSize }) {
	const presence = withAway(usePresence(person.id), person.away)
	const picture = <Avatar {...faceProps(person)} size={size} {...{ presence }} />
	const face =
		onOpen === undefined ? (
			picture
		) : (
			<button
				type="button"
				className={withClass("tb-person-face", className)}
				aria-label={t("people.profile.open", { name: person.name })}
				tabIndex={tabIndex}
				onClick={() => onOpen(person.id)}
			>
				{picture}
			</button>
		)
	return withHoverCard(person, face, hover)
}

/** The name, and the handle where there is room for it. */
export function PersonName({
	person,
	onOpen,
	hover = true,
	tabIndex,
	handle = false,
	status = false,
	className,
	after,
}: PersonPartProps & { handle?: boolean }) {
	const text = (
		<>
			<span className="tb-person-name__text">{person.name}</span>
			{/* Against the name, before anything the account or the surface adds: the glyph is what
			    this person is doing and the pill is what their account is, and the two are not the
			    same kind of fact. */}
			{status ? <StatusMark status={person.status} /> : null}
			<PersonBadge {...{ person }} />
			{after}
			{handle && person.handle !== undefined ? (
				<span className="tb-person-name__handle">{`@${person.handle}`}</span>
			) : null}
		</>
	)
	const name =
		onOpen === undefined ? (
			<span className={withClass("tb-person-name", className)}>{text}</span>
		) : (
			<button
				type="button"
				className={withClass("tb-person-name tb-person-name--button", className)}
				tabIndex={tabIndex}
				onClick={() => onOpen(person.id)}
			>
				{text}
			</button>
		)
	return withHoverCard(person, name, hover)
}

/**
 * A person as one object: the picture, the name, and whatever the surface wants to say under it.
 * One hover card over the two halves rather than two, because they are one target to the eye.
 */
export function PersonChip({
	person,
	size = "sm",
	onOpen,
	hover = true,
	handle = false,
	status = false,
	detail,
	after,
	className,
}: PersonPartProps & {
	size?: AvatarSize
	handle?: boolean
	/** A quiet second line under the name: the switcher's address, a group's member count. */
	detail?: ReactNode
}) {
	const presence = withAway(usePresence(person.id), person.away)
	const body = (
		<>
			<Avatar {...faceProps(person)} size={size} {...{ presence }} />
			<span className="tb-person-chip__text">
				{/* The name is the family's own half, without a card or an opener of its own: the chip
				    is one target, and one hover card over it is what a reader expects. */}
				<PersonName person={person} hover={false} {...{ after, status }} />
				{handle && person.handle !== undefined ? (
					<span className="tb-person-chip__handle">{`@${person.handle}`}</span>
				) : null}
				{detail === undefined ? null : <span className="tb-person-chip__detail">{detail}</span>}
			</span>
		</>
	)
	const chip =
		onOpen === undefined ? (
			<span className={withClass("tb-person-chip", className)}>{body}</span>
		) : (
			<button
				type="button"
				className={withClass("tb-person-chip tb-person-chip--button", className)}
				aria-label={t("people.profile.open", { name: person.name })}
				onClick={() => onOpen(person.id)}
			>
				{body}
			</button>
		)
	return withHoverCard(person, chip, hover)
}

/**
 * The identity block a person or a group is *read* from: the big picture, the name with whatever is
 * true about the account, and the quiet line that says what it is. The profile column, the hover card
 * and a group's page draw the same one — the card is the profile's first lines, which is what makes
 * hovering feel like a shortcut to it rather than a second, smaller product.
 *
 * The size decides the type as well as the picture (`people.css`): the profile's block is the loudest
 * name on its surface, the card's block a step quieter. The heading level is the caller's, because the
 * same block is an `h2` on a page whose `h1` is the frame's and an `h3` in a pane that has a heading of
 * its own.
 *
 * The status stands under the subtitle, in the person's own words. It is drawn here rather than per
 * surface for the same reason the rest of the block is: these are the three places a person is looked
 * up, "In a meeting until 13:00" is what each of them is opened to find out, and a card that showed
 * less than the profile would stop being the profile's first lines.
 */
export function PersonHead({
	person,
	size = "lg",
	level = 3,
	subtitle,
}: {
	person: ChipPerson
	size?: AvatarSize
	/** The level of the name's heading: 2 on a page, 3 inside a pane that is already headed. */
	level?: 2 | 3
	subtitle: string
}) {
	const presence = withAway(usePresence(person.id), person.away)
	const Name = level === 2 ? "h2" : "h3"
	return (
		<div className={`tb-profile__head tb-profile__head--${size}`}>
			<Avatar {...faceProps(person)} size={size} {...{ presence }} />
			<div className="tb-profile__identity">
				<Name className="tb-profile__name">
					{person.name}
					<PersonBadge {...{ person }} />
				</Name>
				{/* The dot is for the eye; here the state is a word, because this is the surface a person
				    is looked up on and a colour is not an answer to a screen reader. It stands at the end
				    of the line that already says what this person is — "Team member · @bea · Online" —
				    rather than beside the name: as a pill in the heading it took the room the name needed, and
				    the column opened on "Bea / Bergmann" over a half-empty second line (X-B1 D14). The
				    class `tb-badge--presence` stays on the element: the visual gate waits for this word
				    to go before it photographs a profile. */}
				<p className="tb-profile__subtitle">
					{subtitle}
					{presence === undefined ? null : (
						<>
							{" · "}
							<span
								className={
									presence === "online"
										? "tb-profile__presence tb-profile__presence--online tb-badge--presence"
										: "tb-profile__presence tb-badge--presence"
								}
							>
								{t(PRESENCE_LABELS[presence])}
							</span>
						</>
					)}
				</p>
				<StatusLine status={person.status} />
			</div>
		</div>
	)
}

/**
 * What the pointer gets after 300 ms over a face or a name: who this is and the one thing anybody
 * wants from a name in a message — a way to write to them. Everything it shows is in the reader's
 * model already, so hovering costs no request; the whole record is one click away in the profile.
 */
function PersonCard({ person }: { person: ChipPerson }) {
	// The surfaces hand over what they happen to know — a message row has a name and a picture out
	// of two maps and no account row at all — so the card looks the person up itself. Everything it
	// needs is in the model the bootstrap filled; a hover is never a request.
	const known = useModel((model) => model.users[person.id])
	const full = known === undefined ? person : chipPerson(known)
	const path = full.handle === undefined ? undefined : directMessagePath(full.handle)
	// The clock of the card's fourth line, ticking with every other clock in the interface
	// (core/i18n/useNow.ts). The zone arrives with the account from the member rung up and is absent
	// for a person the model does not hold, so a card about somebody else's colleague says nothing
	// rather than drawing an empty line. `formatTimeIn` answers "" for a zone it cannot resolve.
	const now = useNow()
	const zone = known?.timezone
	const clock = zone === undefined || zone === "" ? "" : formatTimeIn(now, zone)
	const title = known?.title ?? ""
	return (
		<div className="tb-hovercard__body">
			<PersonHead
				person={full}
				size="md"
				subtitle={known === undefined ? full.name : profileSubtitle(known)}
			/>
			{title === "" && clock === "" ? null : (
				/* The two facts the card adds to the head, as one block: "who is this" is answered by
				   the job before it is answered by the handle (X-B1 D14), and "is it a decent hour to
				   write to this person" is the question a name is hovered with (D-10 A4). */
				<div className="tb-hovercard__facts">
					{title === "" ? null : <p className="tb-hovercard__title">{title}</p>}
					{clock === "" ? null : (
						<p className="tb-hovercard__time">{t("people.time.local", { time: clock })}</p>
					)}
				</div>
			)}
			{path === undefined ? null : (
				<div className="tb-actions">
					<Button
						variant="secondary"
						href={path}
						onClick={inPlaceClick(path)}
						label={t("people.message")}
					/>
				</div>
			)}
		</div>
	)
}

/**
 * The wrapper every part of the family shares, so the card opens the same way over all of them.
 * A name with no account behind it — a message whose author has been purged, a row the model does
 * not know — gets no card: there would be nothing in it.
 */
function withHoverCard(person: ChipPerson, trigger: ReactNode, hover: boolean): ReactNode {
	if (!hover || person.id === 0) return trigger
	return (
		<HoverCard
			label={t("people.card.label", { name: person.name })}
			card={<PersonCard person={person} />}
		>
			{trigger}
		</HoverCard>
	)
}
